import React, { useRef, useState, useLayoutEffect, useEffect } from "react";
import Slider from "react-slick";
import { useIsMobile } from "../../hooks";
import { filterSpecsData } from "../../static-data";

/**
 * @returns {JSX.Element}
 * @constructor
 */

const FilterSpecs = () => {
  const isMobile = useIsMobile("1025");

  const {
    header: { title, description },
    categories,
    filters,
  } = filterSpecsData;

  const [activeCategory, setActiveCategory] = useState(categories[0].id);
  const specs = filters[activeCategory];
  const [filterType, setFilterType] = useState("air");
  const filterSpecs = specs[filterType];

  // Resolve Shop now link: static url vs dynamic (Air3 / Air Mini by selection)
  let shopNowLink = null;
  if (specs.link) {
    if (specs.link.url) {
      shopNowLink = { text: specs.link.text, url: specs.link.url };
    } else {
      shopNowLink = {
        text: specs.link.text,
        url: filterType === "air" ? specs.link.urlAir : specs.link.urlAirMini,
      };
    }
  }

  useLayoutEffect(() => {
    setFilterType(specs.air === null ? "airMini" : "air");
  }, [specs]);

  return (
    <section className="p-filter-specs">
      <div className="container">
        {(title || description) && (
          <div className="p-filter-specs__header">
            {title && <h2 className="p-filter-specs__title">{title}</h2>}
            {description && (
              <div className="p-filter-specs__description">{description}</div>
            )}
          </div>
        )}
        <div
          className={`p-filter-specs__body p-spec-body p-spec-body--${activeCategory}`}
          style={{
            "--primaryColor": specs.primaryColor,
            "--secondaryColor": specs.secondaryColor,
          }}
        >
          <div className="p-spec-body__card">
            <Header {...specs} link={shopNowLink} />
            <Tab
              {...specs}
              filterType={filterType}
              setFilterType={setFilterType}
            />
            {isMobile ? (
              <SpecsSlider {...filterSpecs} link={shopNowLink} />
            ) : (
              <>
                {specs.gridType === "one" && <MasonryOne {...filterSpecs} />}
                {specs.gridType === "two" && <MasonryTwo {...filterSpecs} />}
                {specs.gridType === "three" && (
                  <MasonryThree {...filterSpecs} />
                )}
              </>
            )}
          </div>
          <FilterCategories
            categories={categories}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
          />
        </div>
      </div>
    </section>
  );
};

export default FilterSpecs;

function FilterCategories({ categories, activeCategory, setActiveCategory }) {
  return (
    <div className="p-spec-categories-holder">
      <div className="p-spec-categories">
        <div className="p-spec-categories__overflow">
          <ul className="p-spec-categories__list">
            {categories.map(({ id, name, image }) => (
              <li key={id}>
                <button
                  type="button"
                  className={activeCategory === id ? "active" : ""}
                  onClick={() => setActiveCategory(id)}
                >
                  <img src={image} alt={name} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Header({ title, subtitle, description, link, image }) {
  return (
    <div className="p-spec-body__header">
      <div className="p-spec-body__header-content">
        {title && <h3 className="p-spec-body__title">{title}</h3>}
        {subtitle && <h4 className="p-spec-body__subtitle">{subtitle}</h4>}
        {description && (
          <div className="p-spec-body__description">{description}</div>
        )}
        {link && (
          <a href={link.url} className="button button--primary" target="_blank" rel="noopener noreferrer">
            {link.text}
          </a>
        )}
      </div>
      {image && (
        <div className="p-spec-body__header-img">
          <img src={image.src} alt={title} />
        </div>
      )}
    </div>
  );
}
function Tab({ air, airMini, filterType, setFilterType }) {
  return (
    <div className="p-spec-body__tab">
      <p className="p-spec-body__tab-label">Available for</p>
      <ul className="p-spec-body__tab-list">
        {air && (
          <li>
            <button
              type="button"
              className={`p-spec-body__tab-btn ${
                filterType === "air" ? "active" : ""
              }`}
              onClick={() => setFilterType("air")}
            >
              Mila Air3
            </button>
          </li>
        )}
        {airMini && (
          <li>
            <button
              type="button"
              className={`p-spec-body__tab-btn ${
                filterType === "airMini" ? "active" : ""
              }`}
              onClick={() => setFilterType("airMini")}
            >
              Mila Air Mini
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

function MasonryOne({ gridOne = [], gridTwo = [] }) {
  return (
    <div className="p-spec-massonry">
      {gridOne && (
        <div className="p-spec-item__grid p-spec-item__grid--60">
          {gridOne.map(({ className, label, description }) => (
            <div className={`p-spec-item ${className}`}>
              {label && <strong className="p-spec-item__label">{label}</strong>}
              {description && (
                <div className="p-spec-item__description">{description}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {gridTwo && (
        <div className="p-spec-item__grid p-spec-item__grid--40">
          {gridTwo.map(({ className, label, description }) => (
            <div className={`p-spec-item ${className}`}>
              {label && <strong className="p-spec-item__label">{label}</strong>}
              {description && (
                <div className="p-spec-item__description">{description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function MasonryTwo({ gridOne = [] }) {
  return (
    <div className="p-spec-massonry">
      {gridOne && (
        <div className="p-spec-item__grid">
          {gridOne.map(({ className, label, description }) => (
            <div className={`p-spec-item ${className}`}>
              {label && <strong className="p-spec-item__label">{label}</strong>}
              {description && (
                <div className="p-spec-item__description">{description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function MasonryThree({ gridOne = [], gridTwo = [], gridThree = [] }) {
  return (
    <div className="p-spec-massonry">
      {gridOne && (
        <div className="p-spec-item__grid p-spec-item__grid--37">
          {gridOne.map(({ className, label, description }) => (
            <div className={`p-spec-item ${className}`}>
              {label && <strong className="p-spec-item__label">{label}</strong>}
              {description && (
                <div className="p-spec-item__description">{description}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {gridTwo && (
        <div className="p-spec-item__grid p-spec-item__grid--24">
          {gridTwo.map(({ className, label, description }) => (
            <div className={`p-spec-item ${className}`}>
              {label && <strong className="p-spec-item__label">{label}</strong>}
              {description && (
                <div className="p-spec-item__description">{description}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {gridThree && (
        <div className="p-spec-item__grid p-spec-item__grid--39">
          {gridThree.map(({ className, label, description }) => (
            <div className={`p-spec-item ${className}`}>
              {label && <strong className="p-spec-item__label">{label}</strong>}
              {description && (
                <div className="p-spec-item__description">{description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpecsSlider({ gridOne = [], gridTwo = [], gridThree = [], link }) {
  const allSpecs = [...gridOne, ...gridTwo, ...gridThree];
  const slideRef = useRef(null);
  const settings = {
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: false,
    pauseOnFocus: false,
    speed: 500,
  };

  useEffect(() => {
    if (slideRef.current) {
      slideRef.current.slickGoTo(0);
    }
  }, [allSpecs]);
  return (
    <>
      <Slider className="p-spec-slider" {...settings} ref={slideRef}>
        {allSpecs.map(({ label, description }) => (
          <div className={`p-spec-item p-spec-item--slide`}>
            {label && <strong className="p-spec-item__label">{label}</strong>}
            {description && (
              <div className="p-spec-item__description">{description}</div>
            )}
          </div>
        ))}
      </Slider>
      {link && (
        <div className="p-spec-slider__link">
          <a href={link.url} className="button button--primary" target="_blank" rel="noopener noreferrer">
            {link.text}
          </a>
        </div>
      )}
    </>
  );
}
